import { useFonts } from "expo-font";
import React, { useMemo } from "react";
import { Platform } from "react-native";
import { SafeAreaProvider } from "react-native-safe-area-context";
import { Navigate, Route, Routes } from "react-router";
import { Aurora } from "@lookiero/aurora";
import { bootstrap as lookAndLikeBootstrap, RELEASE as lookAndLikeRelease } from "@lookiero/look-and-like";
import { SentryEnvironment } from "@lookiero/sty-psp-logging/sentry";
import { DummyLayout, themeByTradename } from "@lookiero/sty-psp-ui";
import { apiUrl, kameleoonConfig, Root, styleProfileBootstrap, styleProfileSentryConfig } from "./SharedBootstrap";
import { root } from "./infrastructure/ui/integration/Root";
import { Router } from "./infrastructure/ui/routing/router/Router";
import { isNativeTestVariant, mockServerLookAndLikeApiUrl } from "./testVariant/nativeTestVariant";

const lookAndLikeSentryConfig: SentryEnvironment = {
  publicKey: "a6beababbfccbf57ffdd7408aa9a2b2a",
  release: lookAndLikeRelease,
  project: "4507622227574784",
  environment: "development",
  dist: "web-standalone",
};

const lookAndLikeApiUrl = isNativeTestVariant
  ? mockServerLookAndLikeApiUrl
  : Platform.OS !== "web"
    ? "https://web2.sp.dev.aws.lookiero.es/lookandlike"
    : "http://localhost:3001/lookandlike";

const ExpoRoot: Root = ({ customer, tradename, locale, getAuthToken }) => {
  const [fontsLoaded] = useFonts({
    ["AreaInktrap-Semibold"]: require("@lookiero/aurora/build/assets/fonts/AreaInktrap-Semibold.otf"),
    ["AreaNormal-Semibold"]: require("@lookiero/aurora/build/assets/fonts/AreaNormal-Semibold.otf"),
    ["AreaNormal-Extrabold"]: require("@lookiero/aurora/build/assets/fonts/AreaNormal-Extrabold.otf"),
    ["OpenSans-Regular"]: require("@lookiero/aurora/build/assets/fonts/OpenSans-Regular.ttf"),
    ["OpenSans-Bold"]: require("@lookiero/aurora/build/assets/fonts/OpenSans-Bold.ttf"),
    auroraicons: require("@lookiero/aurora-iconfont/dist/auroraicons.ttf"),
  });

  const theme = useMemo(() => themeByTradename({ tradename }), [tradename]);

  const { Component: Messaging } = useMemo(
    () => styleProfileBootstrap({ apiUrl: () => apiUrl, getAuthToken }),
    [getAuthToken],
  );

  const { root: lookAndLikeRoot } = useMemo(
    () =>
      lookAndLikeBootstrap({
        apiUrl: () => lookAndLikeApiUrl,
        getAuthToken,
        sentry: () => lookAndLikeSentryConfig,
      }),
    [getAuthToken],
  );

  const Root = useMemo(
    () =>
      root({
        Messaging,
        development: true,
        sentry: () => styleProfileSentryConfig,
        kameleoon: () => kameleoonConfig,
        apiUrl: () => apiUrl,
        basePath: "/style-profile",
        getAuthToken,
      })({
        customerId: customer.customerId,
      }),
    [customer.customerId, getAuthToken, Messaging],
  );

  const LookAndLikeRoot = useMemo(
    () =>
      lookAndLikeRoot({
        customerId: customer.customerId,
      }),
    [customer.customerId, lookAndLikeRoot],
  );

  return fontsLoaded ? (
    <SafeAreaProvider>
      <Aurora theme={theme}>
        <Router>
          <Routes>
            <Route
              path="/style-profile/*"
              element={
                <Root
                  customer={customer}
                  layout={DummyLayout}
                  locale={locale}
                  tradename={tradename}
                  lookAndLike={(props) => (
                    <LookAndLikeRoot locale={locale} portalHostName="StyleProfile" tradename={tradename} {...props} />
                  )}
                  onBack={() => {
                    console.log("onBack called");
                  }}
                />
              }
            />

            <Route element={<Navigate to="/style-profile" replace />} path="*" />
          </Routes>
        </Router>
      </Aurora>
    </SafeAreaProvider>
  ) : null;
};

export { ExpoRoot };
